import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Cpu, HardDrive, MemoryStick } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
const formatINR = (value) =>
  value.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
const products = [
  {
    id: 1,
    name: 'Dell Latitude E7470',
    brand: 'Dell',
    processor: 'Intel Core i7-6600U',
    ram: '16GB',
    storage: '512GB SSD',
    price: 38999,
    image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsYXB0b3AlMjBkYXJrfGVufDB8fHx8MTc2ODAxNjUxNnww&ixlib=rb-4.1.0&q=85',
    condition: 'Excellent',
    popular: true,
  },
  {
    id: 2,
    name: 'HP EliteBook 840 G5',
    brand: 'HP',
    processor: 'Intel Core i5-8350U',
    ram: '16GB',
    storage: '256GB SSD',
    price: 32999,
    image: 'https://images.unsplash.com/photo-1572509018340-1fc13b5df491?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxsYXB0b3AlMjBkYXJrfGVufDB8fHx8MTc2ODAxNjUxNnww&ixlib=rb-4.1.0&q=85',
    condition: 'Very Good',
    popular: false,
  },
  {
    id: 3,
    name: 'Lenovo ThinkPad T480',
    brand: 'Lenovo',
    processor: 'Intel Core i7-8550U',
    ram: '16GB',
    storage: '512GB SSD',
    price: 44999,
    image: 'https://images.pexels.com/photos/35505975/pexels-photo-35505975.jpeg',
    condition: 'Excellent',
    popular: true,
  },
  {
    id: 4,
    name: 'Dell XPS 13 9360',
    brand: 'Dell',
    processor: 'Intel Core i5-7200U',
    ram: '8GB',
    storage: '256GB SSD',
    price: 36999,
    image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsYXB0b3AlMjBkYXJrfGVufDB8fHx8MTc2ODAxNjUxNnww&ixlib=rb-4.1.0&q=85',
    condition: 'Good',
    popular: false,
  },
  {
    id: 5,
    name: 'MacBook Air 2019',
    brand: 'Apple',
    processor: 'Intel Core i5',
    ram: '8GB',
    storage: '256GB SSD',
    price: 49999,
    image: 'https://images.unsplash.com/photo-1572509018340-1fc13b5df491?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxsYXB0b3AlMjBkYXJrfGVufDB8fHx8MTc2ODAxNjUxNnww&ixlib=rb-4.1.0&q=85',
    condition: 'Excellent',
    popular: true,
  },
  {
    id: 6,
    name: 'HP ProBook 450 G6',
    brand: 'HP',
    processor: 'Intel Core i7-8565U',
    ram: '16GB',
    storage: '512GB SSD',
    price: 37999,
    image: 'https://images.pexels.com/photos/35505975/pexels-photo-35505975.jpeg',
    condition: 'Very Good',
    popular: false,
  },
];

export const ProductsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedRam, setSelectedRam] = useState('all');
  const [selectedStorage, setSelectedStorage] = useState('all');
  const [priceRange, setPriceRange] = useState('all');

  const brands = ['all', ...new Set(products.map(p => p.brand))];
  const ramOptions = ['all', '8GB', '16GB', '32GB'];
  const storageOptions = ['all', '256GB SSD', '512GB SSD', '1TB SSD'];
  const priceRanges = [
  'all',
  'Under ₹30,000',
  '₹30,000 - ₹40,000',
  '₹40,000 - ₹50,000',
  'Over ₹50,000',
];


  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBrand = selectedBrand === 'all' || product.brand === selectedBrand;
    const matchesRam = selectedRam === 'all' || product.ram === selectedRam;
    const matchesStorage = selectedStorage === 'all' || product.storage === selectedStorage;
    
    let matchesPrice = true;

if (priceRange !== 'all') {
  if (priceRange === 'Under ₹30,000') {
    matchesPrice = product.price < 30000;
  } else if (priceRange === '₹30,000 - ₹40,000') {
    matchesPrice = product.price >= 30000 && product.price < 40000;
  } else if (priceRange === '₹40,000 - ₹50,000') {
    matchesPrice = product.price >= 40000 && product.price < 50000;
  } else if (priceRange === 'Over ₹50,000') {
    matchesPrice = product.price >= 50000;
  }
}


    return matchesSearch && matchesBrand && matchesRam && matchesStorage && matchesPrice;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="products" className="py-16 sm:py-24 bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-web-pattern opacity-30" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 font-['Exo_2']">
            Our <span className="text-gradient-red">Arsenal</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Premium refurbished laptops tested to hero-level standards
          </p>
          <div className="web-separator w-24 mx-auto mt-6" />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 space-y-4"
        >
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              type="text"
              placeholder="Search laptops by name or brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card/50 border-primary/20 focus:border-primary h-12"
            />
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger className="bg-card/50 border-primary/20">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map(brand => (
                  <SelectItem key={brand} value={brand}>
                    {brand === 'all' ? 'All Brands' : brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedRam} onValueChange={setSelectedRam}>
              <SelectTrigger className="bg-card/50 border-primary/20">
                <SelectValue placeholder="RAM" />
              </SelectTrigger>
              <SelectContent>
                {ramOptions.map(ram => (
                  <SelectItem key={ram} value={ram}>
                    {ram === 'all' ? 'All RAM' : ram}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedStorage} onValueChange={setSelectedStorage}>
              <SelectTrigger className="bg-card/50 border-primary/20">
                <SelectValue placeholder="Storage" />
              </SelectTrigger>
              <SelectContent>
                {storageOptions.map(storage => (
                  <SelectItem key={storage} value={storage}>
                    {storage === 'all' ? 'All Storage' : storage}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger className="bg-card/50 border-primary/20">
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                {priceRanges.map(range => (
                  <SelectItem key={range} value={range}>
                    {range === 'all' ? 'All Prices' : range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredProducts.map((product) => (
            <motion.div key={product.id} variants={itemVariants}>
              <Card className="product-card bg-card/50 backdrop-blur-sm border-primary/20 hover:border-primary/50 overflow-hidden group h-full flex flex-col">
                <div className="relative overflow-hidden aspect-video">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {product.popular && (
                    <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground">
                      Popular
                    </Badge>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                <CardHeader>
                  <CardTitle className="text-xl font-bold">{product.name}</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {product.brand} • {product.condition}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 flex-grow">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Cpu className="w-4 h-4 mr-2 text-primary" />
                    {product.processor}
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <MemoryStick className="w-4 h-4 mr-2 text-secondary" />
                    {product.ram} RAM
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <HardDrive className="w-4 h-4 mr-2 text-accent" />
                    {product.storage}
                  </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between border-t border-border pt-4 mt-auto">
                  <div className="flex items-center">
                    
                    <span className="text-2xl font-bold text-primary">
  {formatINR(product.price)}
</span>

                  </div>
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    View Details
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {filteredProducts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-muted-foreground text-lg">No laptops found matching your criteria.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
};